import { createSlice } from '@reduxjs/toolkit';

import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../request-failed/reducer';
import {
  type ILoad,
  type ILoadAvailableValues,
  type ILoadAvailableValuesResponse,
  type ILoadByBrand,
  type ILoadByBrandResponse,
  type ILoadByProperties,
  type ILoadByPropertiesResponse,
  type ILoadCategoryAvailability,
  type ILoadCategoryAvailabilityResponse,
  type ILoadCategoryProperties,
  type ILoadCategoryPropertiesResponse,
  type ILoadFeaturedResponse,
  type ILoadFilteredTotal,
  type ILoadFilteredTotalResponse,
  type ILoadResponse,
  type IProductsState,
  type IRemovePropertyFilter,
  type ISetActiveCategory,
  type ISetPropertyFilter,
} from './types';

const initialState: IProductsState = {
  isLoading: false,
  category: {
    uuid: '',
  },
  products: [],
  pages: 1,
  page: 1,
  categoryProperties: [],
  propertyFilters: [],
  filteredTotal: null,
  availableValues: null,
  availableFeatureFlags: null,
  availableStockOptions: null,
  featured: [],
};

const productsSlice = createSlice({
  name: 'Products',
  initialState,
  reducers: {
    setActiveCategory: (state, action: ISetActiveCategory): void => {
      state.category = { uuid: action.payload.category.uuid };
    },
    load: (state, action: ILoad): void => {
      state.isLoading = true;
      state.category = { uuid: action.payload.category.uuid };
      state.page = action.payload.page;
    },
    loadResponse: (state, action: ILoadResponse): void => {
      state.products = action.payload.products;
      state.isLoading = false;
      state.pages = action.payload.pages;
      state.page = action.payload.page;
    },
    loadByBrand: (state, action: ILoadByBrand): void => {
      state.isLoading = true;
      state.category = { uuid: action.payload.category.uuid };
      state.page = action.payload.page;
    },
    loadByBrandResponse: (state, action: ILoadByBrandResponse): void => {
      state.products = action.payload.products;
      state.isLoading = false;
      state.pages = action.payload.pages;
      state.page = action.payload.page;
    },
    loadCategoryProperties: {
      reducer(state): void {
        state.categoryProperties = [];
      },
      prepare(payload: ILoadCategoryProperties['payload']) {
        return { payload };
      },
    },
    loadCategoryPropertiesResponse: (
      state,
      action: ILoadCategoryPropertiesResponse,
    ): void => {
      state.categoryProperties = action.payload.properties;
    },
    setPropertyFilter: (state, action: ISetPropertyFilter): void => {
      const idx = state.propertyFilters.findIndex(
        (f) => f.code === action.payload.code,
      );

      if (idx >= 0) {
        state.propertyFilters[idx] = action.payload;
      } else {
        state.propertyFilters.push(action.payload);
      }
    },
    removePropertyFilter: (state, action: IRemovePropertyFilter): void => {
      state.propertyFilters = state.propertyFilters.filter(
        (f) => f.code !== action.payload.code,
      );
    },
    clearPropertyFilters: (state): void => {
      state.propertyFilters = [];
      state.filteredTotal = null;
      state.availableValues = null;
      state.categoryProperties = [];
      state.availableFeatureFlags = null;
      state.availableStockOptions = null;
    },
    loadByProperties: (state, action: ILoadByProperties): void => {
      state.isLoading = true;
      state.category = { uuid: action.payload.category.uuid };
      state.page = action.payload.page;
    },
    loadByPropertiesResponse: (
      state,
      action: ILoadByPropertiesResponse,
    ): void => {
      state.products = action.payload.products;
      state.isLoading = false;
      state.pages = action.payload.pages;
      state.page = action.payload.page;
      state.filteredTotal = action.payload.total;
    },
    loadFilteredTotal: {
      reducer(state): void {
        state.filteredTotal = null;
      },
      prepare(payload: ILoadFilteredTotal['payload']) {
        return { payload };
      },
    },
    loadFilteredTotalResponse: (
      state,
      action: ILoadFilteredTotalResponse,
    ): void => {
      state.filteredTotal = action.payload.total;
    },
    resetFilteredTotal: (state): void => {
      state.filteredTotal = null;
    },
    loadAvailableValues: {
      reducer(state): void {
        // keep stale availableValues visible while loading to avoid flicker
        void state;
      },
      prepare(payload: ILoadAvailableValues['payload']) {
        return { payload };
      },
    },
    loadAvailableValuesResponse: (
      state,
      action: ILoadAvailableValuesResponse,
    ): void => {
      state.availableValues = action.payload.availableValues;
    },
    resetAvailableValues: (state): void => {
      state.availableValues = null;
    },
    loadCategoryAvailability: {
      reducer(state): void {
        // keep stale availableStockOptions/availableFeatureFlags visible
        // while loading to avoid flicker
        void state;
      },
      prepare(payload: ILoadCategoryAvailability['payload']) {
        return { payload };
      },
    },
    loadCategoryAvailabilityResponse: (
      state,
      action: ILoadCategoryAvailabilityResponse,
    ): void => {
      state.availableStockOptions = action.payload.availableStockOptions;
      state.availableFeatureFlags = action.payload.availableFeatureFlags;
    },
    loadFeatured: (): void => {},
    loadFeaturedResponse: (state, action: ILoadFeaturedResponse): void => {
      state.featured = action.payload.products;
    },
  },
  extraReducers: (builder): void => {
    builder.addMatcher(
      requestFailedMatcher('Products'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  setActiveCategory,
  load,
  loadResponse,
  loadByBrand,
  loadByBrandResponse,
  loadCategoryProperties,
  loadCategoryPropertiesResponse,
  setPropertyFilter,
  removePropertyFilter,
  clearPropertyFilters,
  loadByProperties,
  loadByPropertiesResponse,
  loadFilteredTotal,
  loadFilteredTotalResponse,
  resetFilteredTotal,
  loadAvailableValues,
  loadAvailableValuesResponse,
  resetAvailableValues,
  loadCategoryAvailability,
  loadCategoryAvailabilityResponse,
  loadFeatured,
  loadFeaturedResponse,
} = productsSlice.actions;

export default productsSlice.reducer;
